import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { projects, cases } from '../services/api';
import { nomUsuari } from '../utils/usuari';

function ProjectDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCaseModal, setShowCaseModal] = useState(false);
  const [caseForm, setCaseForm] = useState({
    title: '',
    description: '',
    instructions: '',
    difficulty: 'intermediate',
    isTeamBased: false,
    timeLimitMinutes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const isOwner = project?.createdBy?.id === user?.id;
  const canEdit = user?.role === 'admin' || isOwner;

  const loadProject = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await projects.getById(id);
      setProject(data);
    } catch (err) {
      setError(err.response?.data?.message || 'Error carregant el projecte');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadProject();
  }, [loadProject]);

  const handlePublish = async () => {
    try {
      await projects.publish(id);
      loadProject();
    } catch (err) {
      setError(err.response?.data?.message || 'Error publicant el projecte');
    }
  };

  const handleArchive = async () => {
    if (!window.confirm('Estàs segur que vols arxivar aquest projecte?')) return;
    try {
      await projects.archive(id);
      navigate('/projects');
    } catch (err) {
      setError(err.response?.data?.message || 'Error arxivant el projecte');
    }
  };

  const handleCreateCase = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const formData = {
        ...caseForm,
        projectId: id,
        timeLimitMinutes: caseForm.timeLimitMinutes ? parseInt(caseForm.timeLimitMinutes, 10) : null
      };
      await cases.create(formData);
      setShowCaseModal(false);
      setCaseForm({
        title: '',
        description: '',
        instructions: '',
        difficulty: 'intermediate',
        isTeamBased: false,
        timeLimitMinutes: ''
      });
      loadProject();
    } catch (err) {
      setError(err.response?.data?.message || 'Error creant el cas');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error && !project) {
    return (
      <div className="text-center py-12">
        <p className="text-error">{error}</p>
        <Link to="/projects" className="text-accent mt-4 inline-block">
          Tornar als projectes
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center space-x-3">
            <Link to="/projects" className="text-content-muted hover:text-content-secondary">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </Link>
            <h1 className="text-2xl font-bold text-content">{project?.title}</h1>
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
              project?.status === 'active' ? 'bg-success-subtle text-success-text' :
              project?.status === 'draft' ? 'bg-warning-subtle text-warning-text' :
              'bg-surface-muted text-content'
            }`}>
              {project?.status}
            </span>
          </div>
          {project?.description && (
            <p className="mt-2 text-content-secondary">{project.description}</p>
          )}
          <p className="mt-1 text-sm text-content-muted">
            Creat per {nomUsuari(project?.createdBy)}
          </p>
        </div>

        {canEdit && (
          <div className="flex space-x-2">
            <Link to={`/projects/${id}/grading`} className="btn btn-secondary">
              Qualificació
            </Link>
            {project?.status === 'draft' && (
              <button onClick={handlePublish} className="btn btn-primary">
                Publicar
              </button>
            )}
            <button onClick={handleArchive} className="btn btn-danger">
              Arxivar
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-error-subtle border border-error/30 text-error-text px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Cases */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-content">Casos</h2>
          {canEdit && (
            <button onClick={() => setShowCaseModal(true)} className="btn btn-primary">
              Afegir cas
            </button>
          )}
        </div>

        {project?.cases?.length === 0 ? (
          <p className="text-content-muted text-center py-8">Encara no hi ha casos</p>
        ) : (
          <div className="space-y-3">
            {project?.cases?.map((caseItem) => (
              <Link
                key={caseItem.id}
                to={`/cases/${caseItem.id}`}
                className="block p-4 bg-surface-subtle rounded-lg hover:bg-surface-muted transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-content">{caseItem.title}</h3>
                    {caseItem.description && (
                      <p className="text-sm text-content-muted mt-1">{caseItem.description}</p>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      caseItem.difficulty === 'beginner' ? 'bg-success-subtle text-success-text' :
                      caseItem.difficulty === 'intermediate' ? 'bg-warning-subtle text-warning-text' :
                      'bg-error-subtle text-error-text'
                    }`}>
                      {caseItem.difficulty}
                    </span>
                    {caseItem.isTeamBased && (
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-accent-subtle text-accent">
                        Equip
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Create Case Modal */}
      {showCaseModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-surface rounded-lg shadow-xl w-full max-w-lg mx-4">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-content mb-4">Afegir cas</h2>
              <form onSubmit={handleCreateCase} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-content-secondary">Títol</label>
                  <input
                    type="text"
                    required
                    value={caseForm.title}
                    onChange={(e) => setCaseForm({ ...caseForm, title: e.target.value })}
                    className="input mt-1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-content-secondary">Descripció</label>
                  <textarea
                    value={caseForm.description}
                    onChange={(e) => setCaseForm({ ...caseForm, description: e.target.value })}
                    className="input mt-1"
                    rows={2}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-content-secondary">Instruccions</label>
                  <textarea
                    value={caseForm.instructions}
                    onChange={(e) => setCaseForm({ ...caseForm, instructions: e.target.value })}
                    className="input mt-1"
                    rows={3}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-content-secondary">Dificultat</label>
                    <select
                      value={caseForm.difficulty}
                      onChange={(e) => setCaseForm({ ...caseForm, difficulty: e.target.value })}
                      className="input mt-1"
                    >
                      <option value="beginner">Principiant</option>
                      <option value="intermediate">Intermedi</option>
                      <option value="advanced">Avançat</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-content-secondary">Límit de temps (minuts)</label>
                    <input
                      type="number"
                      min="0"
                      placeholder="Sense límit"
                      value={caseForm.timeLimitMinutes}
                      onChange={(e) => setCaseForm({ ...caseForm, timeLimitMinutes: e.target.value })}
                      className="input mt-1"
                    />
                    <p className="text-xs text-content-muted mt-1">Deixa buit per sense límit de temps</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isTeamBased"
                    checked={caseForm.isTeamBased}
                    onChange={(e) => setCaseForm({ ...caseForm, isTeamBased: e.target.checked })}
                    className="h-4 w-4 text-primary-600 rounded"
                  />
                  <label htmlFor="isTeamBased" className="ml-2 text-sm text-content-secondary">
                    Cas en equip
                  </label>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCaseModal(false)}
                    className="btn btn-secondary"
                  >
                    Cancel·lar
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="btn btn-primary disabled:opacity-50"
                  >
                    {submitting ? 'Creant...' : 'Crear cas'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProjectDetailPage;
